/**
 * Error thrown by the API layer (`handleApiError`).
 *
 * Wraps the parsed API response body in a real `Error` so that:
 * - Sentry receives a proper Error (readable title + stack) instead of a bare
 *   response object (no stack) reported as "Object captured as exception",
 * - the original backend payload stays reachable via `body` for translation mapping.
 *
 * Lives in its own module (no `@/libs/error-client` import) to avoid a cycle:
 * `utils/api` → `libs/error-client` → `utils/api-error`.
 */
export class ApiError extends Error {
  readonly body: unknown;

  constructor(message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.body = body;
  }
}

/**
 * Returns the underlying API payload, unwrapping `ApiError` when present.
 * Use before reading backend error codes (`body.message`) or `statusCode`.
 */
export const getApiErrorBody = (error: unknown): unknown =>
  error instanceof ApiError ? error.body : error;

/**
 * A single issue from a Zod-style validation envelope, i.e. the generic backend shape
 * `{ message: 'Validation failed', statusCode: 400, errors: ValidationIssue[] }` — distinct from
 * the discriminated `*Error-400` codes that the domain `errors.ts` maps cover.
 */
export type ValidationIssue = {
  path: (string | number)[];
  message: string;
  code: string;
};

const isValidationIssue = (value: unknown): value is ValidationIssue =>
  typeof value === 'object' &&
  value !== null &&
  'path' in value &&
  Array.isArray(value.path) &&
  'message' in value &&
  typeof value.message === 'string' &&
  'code' in value &&
  typeof value.code === 'string';

/**
 * Extracts the nested issues from a validation envelope (unwrapping `ApiError` first). Returns an
 * empty array for any other error shape, so callers can branch on `.length`.
 */
export const getValidationIssues = (error: unknown): ValidationIssue[] => {
  const body = getApiErrorBody(error);

  if (typeof body !== 'object' || body === null || !('errors' in body)) {
    return [];
  }

  const { errors } = body;

  return Array.isArray(errors) ? errors.filter(isValidationIssue) : [];
};
